// src/seoConfig.js
// Single source of truth for per-locale, per-route SEO metadata.
// Used by App.js (runtime <title>/<meta> updates) AND by
// scripts/generate-localized-indexes.js (build-time HTML generation).
//
// IMPORTANT: review the DE/FR/IT/ES strings below with a native speaker
// before shipping. They are written in correct, natural language but a
// human pass always helps.

const SITE_ORIGIN = "https://www.yoursmartcv.com";
const SUPPORTED_LANGS = ["en", "de", "fr", "it", "es"];

// ---------------------------------------------------------------------------
// HOME (/[lang]/)
// ---------------------------------------------------------------------------
const HOME = {
  en: {
    title:
      "YourSmartCV AI CV Builder, ATS CV Fixer & Cover Letter Generator",
    description:
      "Build an ATS-friendly CV with AI. Scan your CV, fix ATS issues, match it to job descriptions, and generate a modern CV and cover letter in minutes.",
  },
  de: {
    title:
      "YourSmartCV KI-Lebenslauf-Builder, ATS-Check & Anschreiben-Generator",
    description:
      "Erstelle einen ATS-freundlichen Lebenslauf mit KI. Prüfe deinen Lebenslauf, optimiere ihn für Stellenanzeigen und erstelle einen modernen Lebenslauf mit Anschreiben.",
  },
  fr: {
    title:
      "YourSmartCV générateur de CV IA, analyse ATS & lettre de motivation",
    description:
      "Créez un CV compatible ATS avec l'IA. Analysez votre CV, corrigez les problèmes ATS, adaptez-le à une offre d'emploi et générez un CV moderne avec lettre de motivation.",
  },
  it: {
    title:
      "YourSmartCV generatore di CV con IA, controllo ATS e lettera di presentazione",
    description:
      "Crea un CV compatibile con ATS usando l'IA. Analizza il tuo CV, correggi i problemi ATS, adattalo a un'offerta di lavoro e genera un CV moderno con lettera di presentazione.",
  },
  es: {
    title:
      "YourSmartCV creador de CV con IA, análisis ATS y carta de presentación",
    description:
      "Crea un CV compatible con ATS usando IA. Analiza tu CV, corrige problemas ATS, adáptalo a una oferta de empleo y genera un CV moderno con carta de presentación.",
  },
};

// ---------------------------------------------------------------------------
// PRICING (/[lang]/pricing)
// ---------------------------------------------------------------------------
const PRICING = {
  en: {
    title:
      "Pricing — YourSmartCV AI CV Builder & ATS Plans",
    description:
      "Choose a plan: Smart CV Conversion (ATS-friendly), AI-Enhanced CV Matching (CV + cover letter to any job), or Premium CV Builder (from scratch). Cancel any time.",
  },
  de: {
    title:
      "Preise — YourSmartCV KI-Lebenslauf & ATS-Pläne",
    description:
      "Wähle deinen Plan: Smart CV Conversion (ATS-freundlich), KI-Lebenslauf-Matching (CV + Anschreiben für jede Stelle) oder Premium CV Builder (von Grund auf). Jederzeit kündbar.",
  },
  fr: {
    title:
      "Tarifs — YourSmartCV générateur de CV IA & plans ATS",
    description:
      "Choisissez votre formule : Smart CV Conversion (compatible ATS), CV optimisé par IA (CV + lettre de motivation pour toute offre) ou Premium CV Builder (à partir de zéro). Résiliable à tout moment.",
  },
  it: {
    title:
      "Prezzi — YourSmartCV generatore di CV con IA e piani ATS",
    description:
      "Scegli il tuo piano: Smart CV Conversion (compatibile ATS), CV ottimizzato con IA (CV + lettera di presentazione per ogni offerta) o Premium CV Builder (da zero). Disdici quando vuoi.",
  },
  es: {
    title:
      "Precios — YourSmartCV creador de CV con IA y planes ATS",
    description:
      "Elige tu plan: Smart CV Conversion (compatible con ATS), CV mejorado con IA (CV + carta de presentación para cualquier oferta) o Premium CV Builder (desde cero). Cancela cuando quieras.",
  },
};

// ---------------------------------------------------------------------------
// ABOUT (/[lang]/about)
// ---------------------------------------------------------------------------
const ABOUT = {
  en: {
    title: "About YourSmartCV — Built by Ly Insights, Berlin",
    description:
      "YourSmartCV helps you build an ATS-friendly CV and cover letter with AI. Built by Ly Insights in Berlin and available in 5 languages.",
  },
  de: {
    title: "Über YourSmartCV — Entwickelt von Ly Insights, Berlin",
    description:
      "YourSmartCV hilft dir, mit KI einen ATS-freundlichen Lebenslauf und ein passendes Anschreiben zu erstellen. Entwickelt von Ly Insights in Berlin, verfügbar in 5 Sprachen.",
  },
  fr: {
    title: "À propos de YourSmartCV — Créé par Ly Insights, Berlin",
    description:
      "YourSmartCV vous aide à créer un CV compatible ATS et une lettre de motivation avec l'IA. Développé par Ly Insights à Berlin, disponible en 5 langues.",
  },
  it: {
    title: "Chi siamo — YourSmartCV, sviluppato da Ly Insights, Berlino",
    description:
      "YourSmartCV ti aiuta a creare un CV compatibile con ATS e una lettera di presentazione con l'IA. Sviluppato da Ly Insights a Berlino, disponibile in 5 lingue.",
  },
  es: {
    title: "Sobre YourSmartCV — Creado por Ly Insights, Berlín",
    description:
      "YourSmartCV te ayuda a crear un CV compatible con ATS y una carta de presentación con IA. Creado por Ly Insights en Berlín, disponible en 5 idiomas.",
  },
};

// ---------------------------------------------------------------------------
// Public registry: route key -> per-language metadata
// "home" matches /[lang]/ and / (root with auto-redirect)
// ---------------------------------------------------------------------------
const SEO_BY_ROUTE = {
  home: HOME,
  pricing: PRICING,
  about: ABOUT,
};

// Map a route key -> URL path (without locale prefix)
const ROUTE_PATHS = {
  home: "/",
  pricing: "/pricing",
  about: "/about",
};

/**
 * Pick the right metadata for the current language + route.
 * Falls back to English if the locale is missing.
 */
function getSeo(routeKey, lang) {
  const route = SEO_BY_ROUTE[routeKey] || SEO_BY_ROUTE.home;
  return route[lang] || route.en;
}

/**
 * Build the canonical URL for a route + language.
 * e.g. canonicalUrl("pricing", "de") -> https://www.yoursmartcv.com/de/pricing
 */
function canonicalUrl(routeKey, lang) {
  const path = ROUTE_PATHS[routeKey] || "/";
  if (path === "/") return `${SITE_ORIGIN}/${lang}/`;
  return `${SITE_ORIGIN}/${lang}${path}`;
}

/**
 * Build the hreflang alternates list for a given route.
 * Returns an array of { hreflang, href } including x-default.
 */
function hreflangAlternates(routeKey) {
  const path = ROUTE_PATHS[routeKey] || "/";
  const alts = SUPPORTED_LANGS.map((l) => ({
    hreflang: l,
    href: path === "/" ? `${SITE_ORIGIN}/${l}/` : `${SITE_ORIGIN}/${l}${path}`,
  }));
  alts.push({
    hreflang: "x-default",
    href: path === "/" ? `${SITE_ORIGIN}/en/` : `${SITE_ORIGIN}/en${path}`,
  });
  return alts;
}

// CommonJS export for the Node build script + ES export for the React app.
// Webpack/CRA reads `module.exports` fine via interop; Node reads it directly.
module.exports = {
  SITE_ORIGIN,
  SUPPORTED_LANGS,
  SEO_BY_ROUTE,
  ROUTE_PATHS,
  getSeo,
  canonicalUrl,
  hreflangAlternates,
};
